import { CloseRounded } from "@mui/icons-material";
import { Box, Button, Grid, IconButton, InputAdornment, Menu, MenuItem, Stack, TextField, Typography, } from "@mui/material";
import SearchIcon from "common/customIcons/search";
import MDButton from "components/MDButton";
import { AgGridTable } from "components/tables/AgGridTable";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CommonLayout from "layouts/DashboardLayout";
import { Link, useNavigate } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import EditIcon from "common/customIcons/edit";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { getTraitListing } from "../../../redux/slices/traitSlice";
import { formatTimestampToDate } from "utilities/common";
import { openCustomModalDialog, } from "../../../redux/slices/modalSlice";
import { openSnackbar } from "../../../redux/slices/common";
import { getSubTraitListing } from "../../../redux/slices/subtraitSlice";
import { useDebounce } from "hooks/useDebounce";
import FilterSubTraits from "common/CommonDrawer/subtraits/FilterTraits";
import { getPermissionsByMode } from "utilities/common";
import { getData } from "utilities/common";
import { hasSectionPermission } from "utilities/common";

export default function ProductSubTraits() {
  const { loading = false, subtraits = {} } = useSelector((state) => state.subtraitSlice);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const searchTermRef = useRef("");
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [currentTraitsTab, setCurrentTraitsTab] = useState("subTrait");
  const traitFiltersPayload = useRef({});
  const mainGridApiRef = useRef("");
  const pageRef = useRef(0);
  const gridRefApi = useRef(null);
  const [parentTraitNames, setParentTraitNames] = useState([]);

  let localUser = getData('login-user-info')
  if (localUser) {
    localUser = JSON.parse(localUser)
  }

  const adminMode = localStorage.getItem("adminsMode") ? JSON.parse(localStorage.getItem("adminsMode")) : null;
  const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);

  const canCreate = hasSectionPermission(permissionsForMode, "Product", "Create");
  const canEdit = hasSectionPermission(permissionsForMode, "Product", "Edit/Update");
  const canView = hasSectionPermission(permissionsForMode, "Product", "View Only");
  const canDelete = hasSectionPermission(permissionsForMode, "Product", "Delete");

  const columnDefs = [
    {
      headerName: "Parent Trait",
      field: "mainTraitName",
      suppressMenu: false,
      minWidth: 140,
      cellRenderer: (props) => {
        if (!props?.value) {
          return "-";
        } else {
          return props?.value;
        }
      },
    },
    {
      headerName: "Name",
      field: "subTraitName",
      suppressMenu: false,
      minWidth: 140,
    },
    {
      headerName: "Platform",
      sortable: false,
      field: "platformAvailability",
      suppressMenu: false,
      minWidth: 100,
      flex: 1,
      cellStyle: { whiteSpace: "normal", lineHeight: "1.5" },
      cellRenderer: (props) => {
        const platformData = props?.value;

        if (!platformData) return "-";

        // If "all" is true, return "All"
        if (platformData.all)
          return (
            <span
              className="tab"

            // className="platform-badge platform-badge-all"
            >
              All
            </span>
          );

        // Filter out keys with `true` values and render them as separate elements
        const activePlatforms = Object.keys(platformData)
          .filter((key) => platformData[key] && key !== "all")
          .map((platform, index) => (
            <span
              key={index}
              className="tab"
            // className="platform-badge"
            >
              {platform}
            </span>
          ));

        return activePlatforms.length > 0 ? activePlatforms : "-";
      },
    },
    {
      headerName: "Updated By",
      field: "updatedBy",
      key: "updatedBy",
      suppressMenu: false,
      minWidth: 180,
      cellRenderer: (props) => {
        if (!props?.value) {
          return "-";
        } else {
          return props?.value;
        }
      },
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: 1.5,
      },
    },
    {
      headerName: "Updated At",
      field: "updatedAt",
      key: "updatedAt",
      suppressMenu: false,
      minWidth: 220,
    },
    // {
    //   headerName: "Status",
    //   field: "status",
    //   suppressMenu: false,
    //   cellRenderer: (props) => {
    //     return <StatusText status={props?.data?.status} />;
    //   },
    //   minWidth: 100,
    // },
    {
      headerName: "Action",
      suppressMenu: true,
      cellRenderer: (props) => {
        return (
          <Box display="inline-flex" alignItems="center">
            <IconButton
              disabled={!canEdit ? true : false}
              onClick={(e) => {
                e.stopPropagation();
                onClickEdit(props);
              }}
              color="primary"
              size="small"
              variant="primary"
            >
              <EditIcon width="16px" height="16px" />
            </IconButton>
          </Box>
        );
      },
      cellStyle: { textAlign: "right", justifyContent: "end" },
      headerClass: "right-aligned-cell-header",
      minWidth: 120,
      maxWidth: 100,
      pinned: "right",
    },
  ];

  function getSortInfo(data) {
    const result = {};
    data.forEach((item) => {
      if (item?.colId === "subTraitName") {
        result[item.colId] = item.sort === "asc" ? "high" : "low";
      } else if (item?.colId === "mainTraitName") {
        result[item.colId] = item.sort === "asc" ? "high" : "low";
      } else if (item?.colId === "updatedAt") {
        result[item.colId] = item.sort === "asc" ? "high" : "low";
      }
    });

    return result;
  }

  const getRows = async (params) => {
    gridRefApi.current = params;
    let filter = {};
    if (params?.sortModel.length) {
      filter = { ...getSortInfo(params?.sortModel) };
    }
    dispatch(
      getSubTraitListing({
        skip: pageRef.current,
        searchTerm: searchTermRef.current,
        limit: 20,
        userType: "admin",
        ...filter,
      })
    )
      .unwrap()
      .then((res) => {
        const { subTraits, total } = res;
        const subTraitsArray = subTraits?.map((item) => {
          return { ...item, id: item._id, updatedAt: formatTimestampToDate(item?.updatedDate) };
        });
        params.successCallback(subTraitsArray, total);
        pageRef.current = pageRef.current + 20;
      });
  };

  const searchFn = () => {
    const dataSource = {
      getRows,
    };
    if (mainGridApiRef.current) {
      mainGridApiRef.current.ensureIndexVisible(0, null);
      mainGridApiRef.current.setGridOption("datasource", dataSource);
    }
  };
  const handleSearch = useDebounce(() => {
    pageRef.current = 0;
    searchFn();
  }, 500);

  const searchHandler = (searchValue) => {
    setSearchTerm(searchValue);
    searchTermRef.current = searchValue;
    handleSearch(searchValue);

    // Debounce the search callback
  };

  const onRowClicked = (row) => {
    if (row?.colDef?.headerName !== "Action" && row?.colDef?.headerName !== "Product Name/SKU") {
      navigate(`/product_traits/subtrait/${row?.data?.subTraitName}`);
      // navigate(`/product_traits/subtrait/${row?.data?.traitSlug}`);

    }
  };

  const onClickEdit = (props) => {
    navigate(`/product_traits/subtrait/${props?.data?.subTraitName}`);
    // navigate(`/product_traits/subtrait/${props?.data?.traitSlug}`);

  };

  const showError = (message, errortype) => {
    if (errortype === "success") {
      dispatch(openSnackbar({ content: message, color: "success", icon: "check" }));
    }
    if (errortype === "error") {
      dispatch(openSnackbar({ content: message, color: "error", icon: "warning" }));
    }
  };

  const resetData = () => {
    pageRef.current = 0;
    if (mainGridApiRef.current) {
      mainGridApiRef.current.forEachNode((node) => {
        node.setSelected(false);
      });
    }

    searchFn();
    setAnchorEl(null);
  };

  const bulkUpdate = (type = "") => {
    // Assuming you have a reference to your Ag-Grid instance, e.g., agGridInstance
    const selectedRows = mainGridApiRef.current.getSelectedRows();
    // Extracting IDs from selected rows
    const selectedIds = selectedRows.map((row) => row.productId);
    if (selectedIds?.length > 0) {
      setAnchorEl(null);
      dispatch(
        openCustomModalDialog({
          modalData: {
            bulkProductIds: selectedIds,
            resetData,
          },
          modal: type || "BULK_DELETE_PRODUCT",
        })
      );
    }
  };

  useEffect(() => {
    const fetchParentTraits = async () => {
      try {
        const result = await dispatch(getTraitListing()).unwrap();
        await setParentTraitNames(result?.traits);
      } catch (error) {
      }
    };

    fetchParentTraits();
  }, []);

  const onSelectionChanged = () => {
    const selectedRows = mainGridApiRef.current.getSelectedRows();
    if (selectedRows?.length > 0) {
      setShowBulkUpdate(true);
    } else {
      setShowBulkUpdate(false);
    }
  };
  const applyTraitFilter = (filterPayload) => {
    pageRef.current = 0;
    traitFiltersPayload.current = filterPayload;
    const dataSource = {
      getRows: (params) => {
        dispatch(
          getSubTraitListing({
            skip: pageRef.current,
            searchTerm: searchTermRef.current,
            limit: 20,
            userType: "admin",
            ...filterPayload?.queryPayload,
          })
        )
          .unwrap()
          .then((res) => {
            const { subTraits, total } = res;
            const subTraitsArray = subTraits?.map((item) => {
              return { ...item, id: item._id, createdAt: formatTimestampToDate(item?.createdDate) };
            });
            params.successCallback(subTraitsArray, total);
            pageRef.current = pageRef.current + 20;
          });
      },
    };
    if (mainGridApiRef?.current) {
      mainGridApiRef.current.ensureIndexVisible(0, null);
      mainGridApiRef.current.setGridOption("datasource", dataSource);
    }
  };

  useEffect(() => {
    if (mainGridApiRef?.current) {
      if (loading) {
        mainGridApiRef.current.showLoadingOverlay();
      } else if (subtraits?.total === 0) {
        mainGridApiRef.current.showNoRowsOverlay();
      } else {
        mainGridApiRef.current.hideOverlay();
      }
    }
  }, [subtraits, loading]);

  const handleTabChange = (tab) => {
    setCurrentTraitsTab(tab);

    if (tab === "mainTraits") {
      navigate("/product_traits"); // Navigate to the Traits URL
    } else if (tab === "subTrait") {
      navigate("/product_traits/subtrait"); // Navigate to the subTraits URL
    }
  };

  return (
    <CommonLayout>
      {

        <Box p={{ xs: 2, sm: 3, lg: 4 }} pb={{ lg: 2 }} className="row-pointer">
          <Box mb={2} className="cannabinoids-tabs">
            <Stack direction="row" gap={1} sx={{ background: "#ffffff" }}>
              <Button
                onClick={() => handleTabChange("mainTraits")}
                variant="outlined"
                sx={{ color: "#2E2E3A", minWidth: "180px" }}
                className={currentTraitsTab === "mainTraits" ? "active" : ""}
              >
                Major Traits
              </Button>
              <Button
                className={currentTraitsTab === "subTrait" ? "active" : ""}
                onClick={() => handleTabChange("subTrait")}
                variant="outlined"
                sx={{ color: "#2E2E3A", minWidth: "180px" }}
              >
                Sub Traits
              </Button>
            </Stack>
          </Box>

          <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={2}>
            <Grid item xs={12} sm={2}>
              <Typography variant="h4">Subtraits</Typography>
            </Grid>

            <Grid item xs={12} sm={10}>
              <Stack
                direction="row"
                justifyContent={{ xs: "start", sm: "end" }}
                flexWrap="wrap"
                alignItems="stretch"
                gap={2}
              >
                {/* <Search  placeholder='Search' label='' value={searchTerm} onChange={searchHandler} /> */}

                <TextField
                  className="custom-search-field"
                  placeholder="Search"
                  label=""
                  value={searchTerm}
                  onChange={(e) => searchHandler(e.target.value)}
                  sx={{ minWidth: "280px" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconButton size="small">
                          <SearchIcon width="18px" height="18px" />
                        </IconButton>
                      </InputAdornment>
                    ),
                    endAdornment:
                      searchTerm?.length > 0 ? (
                        <InputAdornment>
                          <IconButton onClick={() => searchHandler("")} size="small">
                            <CloseRounded />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                  }}
                />
                {
                  canCreate &&
                  <Link to="/product_traits/subtrait/add">
                    <MDButton

                      style={{ height: "100%", minWidth: "140px" }}
                      startIcon={<AddRoundedIcon fontSize="large" />}
                      variant="contained"
                      color="primary"
                    >
                      New
                    </MDButton>
                  </Link>
                }

                {
                  <>
                    {/* <MDButton disabled={!showBulkUpdate} variant='contained' color='warning' onClick={(e) => setAnchorEl(e.currentTarget)}>Bulk Update</MDButton> */}
                    <Menu
                      id="basic-menu"
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={() => setAnchorEl(null)}
                      sx={{ mt: 0.5 }}
                    >
                      <MenuItem onClick={() => bulkUpdate("BULK_UPDATE_STATUS")}>
                        Change Status
                      </MenuItem>
                      <MenuItem onClick={() => bulkUpdate("BULK_DELETE_PRODUCT")}>
                        Delete Product
                      </MenuItem>
                      <MenuItem onClick={() => bulkUpdate("BULK_UPDATE_BRAND")}>
                        Change Brand
                      </MenuItem>
                      <MenuItem onClick={() => bulkUpdate("BULK_UPDATE_PRICE")}>Edit Price</MenuItem>
                      <MenuItem onClick={() => bulkUpdate("BULK_UPDATE_SALE_PRICE")}>
                        Edit Sale Price
                      </MenuItem>
                      <MenuItem onClick={() => bulkUpdate("BULK_UPDATE_CATEGORY")}>
                        Edit Category
                      </MenuItem>
                      <MenuItem onClick={() => bulkUpdate("BULK_UPDATE_TAGS")}>Edit Tags</MenuItem>
                      {/* <MenuItem >Change Flower Type</MenuItem> */}
                    </Menu>
                  </>
                }
              </Stack>
            </Grid>
          </Grid>
          <FilterSubTraits applyTraitFilter={applyTraitFilter} parentTraits={parentTraitNames} />
          {canView &&
            <AgGridTable
              col={columnDefs}
              mainGridApiRef={mainGridApiRef}
              getRows={getRows}
              pageRef={pageRef}
              isLoading={loading}
              onRowClicked={onRowClicked}
              style={{ height: "56vh" }}
              gridOptions={{
                rowHeight: 100,
              }}
              onSelectionChanged={onSelectionChanged}
            />
          }
        </Box>
      }
    </CommonLayout>
  );
}
